import { useQuery } from '@tanstack/react-query';

import type { Post } from '@/entities/post/types';
import type { SavedQueryBody } from '@/entities/query/types';
import { socialApiClient } from '@/shared/api-client/social-api-client';

async function previewQuery(
  {
    workspaceId,
    queryId,
    filter,
    options,
  }: {
    workspaceId: string;
    queryId: string;
  } & SavedQueryBody,
  signal?: AbortSignal,
): Promise<{
  posts: Post[];
  total: number;
  next_token: string;
}> {
  const response = await socialApiClient.post<{
    posts: Array<Post>;
    total: number;
    next_token: string;
  }>(`workspaces/${workspaceId}/queries/${queryId}/preview`, {
    json: {
      filter,
      options,
    },
    signal,
  });

  const data = await response.json();

  return {
    posts: data.posts,
    total: data.total,
    next_token: data.next_token,
  };
}

export function usePreviewQuery(
  params:
    | ({
        workspaceId: string;
        queryId: string;
      } & SavedQueryBody)
    | null,
) {
  return useQuery({
    queryKey: ['query-preview', params],
    queryFn: ({ signal }) => previewQuery(params!, signal),
    enabled: !!params,
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't keep in cache after unmount
  });
}
